import { ArrowBack } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, useFormikContext, Field } from "formik";
import InputField from "../../common/FormFields/InputField";
import SelectField from "../../common/FormFields/SelectField";
import RatingAutocompleteField from "./RatingAutocompleteField";
import {
  DisabledJobRatings,
  JobRating,
  JobRatingLabel,
} from "../../common/JobOrder";
import {
  Gender,
  GenderLabel,
  MarinerProfile,
  MarinerStatus,
  MarinerStatusLabel,
  MarinerType,
  MarinerUnion,
  TrainingStatus,
  TrainingStatusLabel,
} from "../../common/MarinerProfile";
import convertDateToString from "../../util/ConvertDateToString";
import {
  useCreateMarinerMutation,
  useUpdateMarinerMutation,
  useAdminCreateProfileUserMutation,
  useAdminResendRegistrationEmailMutation,
} from "../../redux/api/mariner";
import initialValues from "./formValidation/initialValues";
import formData from "./formValidation/formData";
import validationSchema from "./formValidation/validationSchema";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import {
  selectCurrentProfile,
  selectCurrentUser,
} from "../../redux/authentication/authSlice";
import { UserGroup } from "../../common/User";
import { usePrompt } from "../../util/usePrompt";
import { marinerStatusText } from "../../common/CognitoUser/MarinerStatusText";
import { registrationEmailButton } from "../../common/CognitoUser/RegistrationEmailButton";
import { useGetVesselsQuery } from "../../redux/api/vessel";
import AutocompleteField from "../../common/FormFields/AutocompleteField";

interface MarinerProfileFormProps {
  marinerProfile?: MarinerProfile;
  isLoading?: boolean;
  isFetching?: boolean;
  refetch?: () => void;
}

const MarinerProfileForm = ({ marinerProfile, isLoading, isFetching, refetch }: MarinerProfileFormProps) => {
  const navigate = useNavigate();
  const { formId, formField } = formData;
  const {
    firstName,
    middleName,
    lastName,
    mmcId,
    dateOfBirth,
    gender,
    rating,
    type,
    agentUnion,
    status,
    twicTsaNum,
    phone,
    email,
    placeOfBirth,
    seniority,
    veteran,
    trainingStatus,
    vessel,
  } = formField;

  const { id } = useParams();

  const user = useSelector(selectCurrentUser);
  const userGroup = user?.userGroup as UserGroup;
  const userProfile: MarinerProfile = useSelector(selectCurrentProfile);
  // temporarily store the status in this variable, this changes with each render and is loaded when the form is loaded
  let profileUserStatus = "";
  // store whether the profile already had an email saved on load
  let emailExists = false;
  const {
    data: vessels,
    isLoading: isVesselLoading,
    isFetching: isVesselFetching,
  } = useGetVesselsQuery({});

  const [createMarinerProfile] = useCreateMarinerMutation();
  const [updateMarinerProfile] = useUpdateMarinerMutation();
  const [adminCreateProfileUser] = useAdminCreateProfileUserMutation();
  const [adminResendRegistrationEmail] =
    useAdminResendRegistrationEmailMutation();

  let getInitialValues = () => {
    if (!isLoading && !isFetching && id !== undefined && id !== "new") {
      let res: any = JSON.parse(JSON.stringify(marinerProfile));
      Object.keys(res || {}).forEach(
        (key) => (res[key] = res[key] === null ? "" : res[key])
      );
      // store the user status
      profileUserStatus = res["cognitoUser"]
        ? res["cognitoUser"]["status"]
        : "";
      // store whether the profile already had an email saved on load
      emailExists = !!res["email"];
      return {
        [firstName.name]: res["firstName"],
        [middleName.name]: res["middleName"],
        [lastName.name]: res["lastName"],
        [mmcId.name]: res["mmcId"],
        [dateOfBirth.name]:
          res["dateOfBirth"] !== undefined && res["dateOfBirth"] !== ""
            ? convertDateToString(new Date(res["dateOfBirth"]))
            : res["dateOfBirth"],
        [gender.name]: res["gender"],
        [rating.name]: res["rating"],
        [type.name]: res["type"],
        [agentUnion.name]: res["agentUnion"],
        [status.name]: res["status"],
        [twicTsaNum.name]: res["twicTsaNum"],
        [phone.name]: res["phone"],
        [email.name]: res["email"],
        [placeOfBirth.name]: res["placeOfBirth"],
        [seniority.name]: res["seniority"] === "" ? null : res["seniority"],
        [veteran.name]: res["veteran"],
        [trainingStatus.name]: res["trainingStatus"],
        [vessel.name]: res["vessel"],
      };
    } else {
      return initialValues;
    }
  };

  const getSeniority = (values: any) => {
    if (values.type === MarinerType.LICENSED) {
      return "NA";
    } else if (
      [
        JobRating._OSA2,
        JobRating._OSA3,
        JobRating._WA2,
        JobRating._WA3,
      ].includes(values.rating)
    ) {
      return "U";
    } else {
      return values.seniority;
    }
  };

  const buildUpdatedMarinerProfile = (values: any) => {
    const marinerProfile: MarinerProfile = {
      firstName: values.firstName.trim() === "" ? null : values.firstName,
      middleName: values.middleName,
      lastName: values.lastName.trim() === "" ? null : values.lastName,
      mmcId: values.mmcId.trim() === "" ? null : values.mmcId,
      dateOfBirth: values.dateOfBirth,
      gender: values.gender || null,
      rating: values.rating || null,
      type: values.type || null,
      status: values.status || null,
      agentUnion: values.agentUnion || null,
      twicTsaNum: values.twicTsaNum === "" ? null : values.twicTsaNum,
      phone: values.phone === "" ? null : values.phone,
      email: values.email === "" ? null : values.email,
      placeOfBirth: values.placeOfBirth,
      seniority: getSeniority(values),
      user: undefined,
      veteran: values.veteran,
      trainingStatus:
        values.trainingStatus === "" ? null : values.trainingStatus,
      vessel: values.vessel?.id ? values.vessel : undefined,
    };

    return marinerProfile;
  };

  async function _submitForm(values: any, actions: any) {
    let profileId = Number(id);
    if (!!id && id !== "new") {
      const updateResult = await updateMarinerProfile({
        profileId: id,
        profile: buildUpdatedMarinerProfile(values),
      });
      if ("error" in updateResult) {
        actions.setSubmitting(false);
        return;
      }
    } else {
      const createResult = await createMarinerProfile(
        buildUpdatedMarinerProfile(values)
      );
      if ("error" in createResult) {
        actions.setSubmitting(false);
        return;
      }
      profileId = createResult.data.id as number;
    }
    actions.setSubmitting(false);
    !id || id === "new" ? navigate(`/profiles/${profileId}`) : refetch?.();
    toast.success(
      <p>
        Mariner Profile {!!id && id !== "new" ? "updated" : "created"}{" "}
        successfully.
      </p>,
      {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      }
    );
  }

  const IsFormDirty = () => {
    const { dirty } = useFormikContext();
    usePrompt("Changes will not be saved, would you like to leave?", dirty);
    return <></>;
  };

  function _handleSubmit(values: any, actions: any) {
    if (!id || id === "new") {
      actions.resetForm({ values });
    }
    _submitForm(values, actions);
  }

  if (userGroup === UserGroup.MARINERS && !userProfile) {
    return (
      <Box sx={{ display: "flex" }}>
        <CircularProgress />
      </Box>
    );
  }

  async function adminRegisterUser() {
    if (!!id && id !== "new") {
      const res = await adminCreateProfileUser(id);
      if ("error" in res) {
        return;
      }
    } else {
      toast.error(<p>Profile must have a saved email before registering.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }
    toast.success(<p>Registration email sent successfully.</p>, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
    refetch?.();
  }

  async function resendRegistrationEmail() {
    if (!!id && id !== "new") {
      const res = await adminResendRegistrationEmail(id);
      if ("error" in res) {
        return;
      }
    } else {
      toast.error(<p>Profile must have a saved email before registering.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }
    toast.success(<p>Registration email re-sent successfully.</p>, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
    refetch?.();
  }

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={_handleSubmit}
      >
        {({
          isSubmitting,
          values,
          handleChange,
          handleBlur,
          setFieldValue,
          errors,
          touched,
        }) => (
          <Form id={formId}>
            <IsFormDirty />
            <div className="page-header align-items-center justify-content-btwn">
              <div className="flex g-3 marinerprofile-header">
                <h1>Mariner Profile</h1>
                {!(userGroup === UserGroup.MARINERS && userProfile.id?.toString() !== id) &&
                    <Button
                  variant="contained"
                  className="blue-button"
                  type="submit"
                  disabled={isSubmitting}
                >
                  Save Changes
                </Button>}
              </div>

              <Button className="blue-link-button" onClick={() => navigate(-1)}>
                {" "}
                <ArrowBack />
                Back
              </Button>
            </div>
            {isSubmitting && <CircularProgress size={24} />}
            <Paper>
              <div className="registration-header">
                <h3>Mariner Information</h3>
                {/* do not allow Mariners to see registration status / email button */}
                {userGroup !== UserGroup.MARINERS && !!id && id !== "new" && (
                  <div className="registration-status">
                    {marinerStatusText(profileUserStatus)}
                    {registrationEmailButton(
                      profileUserStatus,
                      emailExists,
                      adminRegisterUser,
                      resendRegistrationEmail,
                      userGroup === UserGroup.CREWING_EXECS ||
                        userGroup === UserGroup.HUMAN_RESOURCES ||
                        userGroup === UserGroup.HUMAN_RESOURCES_ADMINS
                    )}
                  </div>
                )}
              </div>
              <div className="form-wide">
                <label>
                  <span className="required">{mmcId.label}</span>
                  <InputField
                    name={mmcId.name}
                    fullWidth
                    size="small"
                    type="text"
                    disabled={userGroup === UserGroup.MARINERS}
                  />
                </label>
                <label>
                  <span className="required">{firstName.label}</span>
                  <InputField
                    name={firstName.name}
                    fullWidth
                    size="small"
                    type="text"
                    disabled={
                      userGroup === UserGroup.MARINERS &&
                      userProfile.id?.toString() !== id
                    }
                  />
                </label>
                <label>
                  <span>{middleName.label}</span>
                  <InputField
                    name={middleName.name}
                    fullWidth
                    size="small"
                    type="text"
                    disabled={
                      userGroup === UserGroup.MARINERS &&
                      userProfile.id?.toString() !== id
                    }
                  />
                </label>
                <label>
                  <span className="required">{lastName.label}</span>
                  <InputField
                    name={lastName.name}
                    fullWidth
                    size="small"
                    type="text"
                    disabled={
                      userGroup === UserGroup.MARINERS &&
                      userProfile.id?.toString() !== id
                    }
                  />
                </label>
                <label>
                  <span className="flex">
                    {status.label}
                    <Tooltip
                      title={
                        <>
                          <Typography variant="caption" display="block">
                            On board – currently signed on a vessel
                          </Typography>
                          <Typography variant="caption" display="block">
                            Active – not signed on but can be chosen for a job
                          </Typography>
                          <Typography variant="caption" display="block">
                            Pending – a Specialist is working on them with a
                            particular job in mind
                          </Typography>
                          <Typography variant="caption" display="block">
                            Unavailable – currently on LOA, FMLA, or given a
                            specific return date
                          </Typography>
                          <Typography variant="caption" display="block">
                            Inactive – has not been employed by TOTE for over 2
                            years
                          </Typography>
                        </>
                      }
                      placement="right"
                    >
                      <HelpOutlineIcon fontSize="small" />
                    </Tooltip>
                  </span>
                  <SelectField
                    name={status.name}
                    fullWidth
                    size="small"
                    data={Object.entries(MarinerStatus).map(([key, value]) => ({
                      value: value,
                      label: MarinerStatusLabel[key as MarinerStatus],
                    }))}
                    hideBlank={true}
                    disabled={userGroup === UserGroup.MARINERS}
                  />
                </label>
                <label>
                  <span>{rating.label}</span>
                  <RatingAutocompleteField
                    name={rating.name}
                    typeName={type.name}
                    agentUnionName={agentUnion.name}
                    seniorityName={seniority.name}
                    data={Object.values(JobRating).filter(
                      (rating) =>
                        !DisabledJobRatings.has(
                          JobRating[rating as keyof typeof JobRating]
                        )
                    )}
                    setFieldValue={setFieldValue}
                    getOptionLabel={(option: string) =>
                      JobRatingLabel[
                        JobRating[option as keyof typeof JobRating]
                      ]
                    }
                    disabled={userGroup === UserGroup.MARINERS}
                  />
                </label>
                <label>
                  <span>{type.label}</span>
                  <InputField
                    name={type.name}
                    fullWidth
                    size="small"
                    type="text"
                    disabled
                  />
                </label>
                <label>
                  <span>{agentUnion.label}</span>
                  <SelectField
                    name={agentUnion.name}
                    fullWidth
                    size="small"
                    data={[
                      {
                        value: MarinerUnion.NON_UNION,
                        label: MarinerUnion.NON_UNION.valueOf(),
                      },
                      values?.type === MarinerType.LICENSED
                        ? {
                            value: MarinerUnion.AMO,
                            label: MarinerUnion.AMO.valueOf(),
                          }
                        : {
                            value: MarinerUnion.SIU,
                            label: MarinerUnion.SIU.valueOf(),
                          },
                    ]}
                    hideBlank={true}
                    disabled={userGroup === UserGroup.MARINERS}
                  />
                  {/* <InputField name={agentUnion.name} fullWidth size="small" type="text" disabled /> */}
                </label>
                {!!values &&
                  values.agentUnion === MarinerUnion.SIU &&
                  ![
                    JobRating._OSA2,
                    JobRating._OSA3,
                    JobRating._WA2,
                    JobRating._WA3,
                  ].includes(values.rating) && (
                    <label>
                      <span>{seniority.label}</span>
                      <SelectField
                        name={seniority.name}
                        data={[
                          { value: "NA", label: "NA" },
                          { value: "A", label: "A" },
                          { value: "B", label: "B" },
                          { value: "C", label: "C" },
                          { value: "U", label: "U" },
                          { value: null, label: "" },
                        ]}
                        fullWidth
                        size="small"
                        hideBlank={true}
                        disabled={userGroup === UserGroup.MARINERS}
                      />
                    </label>
                  )}
                <label>
                  <span>{twicTsaNum.label}</span>
                  <InputField
                    name={twicTsaNum.name}
                    fullWidth
                    size="small"
                    type="text"
                    disabled={userGroup === UserGroup.MARINERS}
                  />
                </label>
                <label>
                  <span>{phone.label}</span>
                  <InputField
                    name={phone.name}
                    placeholder="(xxx) xxx-xxxx"
                    fullWidth
                    size="small"
                    type="text"
                    disabled={
                      userGroup === UserGroup.MARINERS &&
                      userProfile.id?.toString() !== id
                    }
                  />
                </label>
                <label>
                  <span>{email.label}</span>
                  <InputField
                    name={email.name}
                    placeholder="example@domain.com"
                    fullWidth
                    size="small"
                    type="text"
                    disabled={
                      userGroup === UserGroup.MARINERS &&
                      userProfile.id?.toString() !== id
                    }
                  />
                </label>
                <label>
                  <span>{dateOfBirth.label}</span>
                  <InputField
                    name={dateOfBirth.name}
                    fullWidth
                    size="small"
                    type="date"
                    disabled={
                      userGroup === UserGroup.MARINERS &&
                      userProfile.id?.toString() !== id
                    }
                  />
                </label>
                <label>
                  <span>{placeOfBirth.label}</span>
                  <InputField
                    name={placeOfBirth.name}
                    fullWidth
                    size="small"
                    type="text"
                    disabled={
                      userGroup === UserGroup.MARINERS &&
                      userProfile.id?.toString() !== id
                    }
                  />
                </label>
                <label>
                  <span>{gender.label}</span>
                  <SelectField
                    name={gender.name}
                    fullWidth
                    size="small"
                    data={Object.entries(Gender).map(([key, value]) => ({
                      value: value,
                      label: GenderLabel[key as Gender],
                    }))}
                    hideBlank={true}
                    disabled={
                      userGroup === UserGroup.MARINERS &&
                      userProfile.id?.toString() !== id
                    }
                  />
                </label>
                {userGroup === UserGroup.MARINERS && (
                  <label>
                    <span>{veteran.label}</span>
                    <Field name={veteran.name} type="checkbox" 
                      disabled={
                      userGroup === UserGroup.MARINERS &&
                      userProfile.id?.toString() !== id
                    }/>
                  </label>
                )}
                {userGroup !== UserGroup.MARINERS && (
                  <label>
                    <span>{trainingStatus.label}</span>
                    <SelectField
                      name={trainingStatus.name}
                      fullWidth
                      size="small"
                      data={Object.entries(TrainingStatus).map(
                        ([key, value]) => ({
                          value: value,
                          label: TrainingStatusLabel[key as TrainingStatus],
                        })
                      )}
                      hideBlank={true}
                    />
                  </label>
                )}
                {userGroup !== UserGroup.MARINERS &&
                  !isVesselLoading &&
                  !isVesselFetching && (
                    <label>
                      <span>{vessel.label}</span>
                      <AutocompleteField
                        name={vessel.name}
                        data={
                          !!vessels ? vessels.filter((v) => !v.disabled) : []
                        }
                        setFieldValue={setFieldValue}
                        getOptionLabel={(vessel: { name: any }) => {
                          return vessel?.name ? vessel.name : "";
                        }}
                        disabled={
                          !(
                            userGroup === UserGroup.CREWING_SPECIALISTS ||
                            userGroup === UserGroup.CREWING_EVAL_SPECIALISTS ||
                            userGroup === UserGroup.CREWING_MANAGERS ||
                            userGroup === UserGroup.HUMAN_RESOURCES_ADMINS
                          ) ||
                          !(
                            values.trainingStatus === TrainingStatus.ACTIVE ||
                            values.trainingStatus === TrainingStatus.PERMANENT
                          )
                        }
                      />
                    </label>
                  )}
              </div>
            </Paper>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default MarinerProfileForm;
